import { Api_Video_Type, Enum_LiveStream_Auto_Response, Enum_LiveStream_Private_Messaging, Enum_LiveStream_Provider, Enum_LiveStream_Status, } from '@core/api';
import { ILiveStreamState } from '@embed/components/VideoPlayer/Player3/store/types';
/** Simple check for video type "live_stream" */
export const isLiveStream = (video) => video?.video_type === Api_Video_Type.LIVESTREAM;
/** Determine whether video is a valid livestream replay (based on status and `replay_enabled` flag) */
export function isLiveStreamReplay(video) {
    return (video?.live_stream_status === Enum_LiveStream_Status.REPLAY &&
        !!video.live_stream_replay_enabled &&
        !!video.live_stream_replay_url);
}
/** Determine whether video is a livestream replay with `replay_enabled` flag disabled */
export function isLivestreamReplayDisabled(video) {
    return (video?.live_stream_status === Enum_LiveStream_Status.REPLAY &&
        !video.live_stream_replay_enabled);
}
/**
 * Determine whether the video is an active livestream (based on status).
 * [CS-9765] We also check if the video has a valid playback URL or a valid replay
 * URL with CDN provider.
 */
export function isLiveStreamLive(video) {
    return ((video?.live_stream_status === Enum_LiveStream_Status.ACTIVE ||
        video?.live_stream_status === Enum_LiveStream_Status.PAUSED) &&
        (!!video?.live_stream_playback_url ||
            (!!video?.live_stream_replay_url &&
                video?.live_stream_provider === Enum_LiveStream_Provider.CDN)));
}
/** Determine whether the video is active or replay livestream */
export function isLiveStreamLiveOrReplay(video) {
    return isLiveStreamLive(video) || isLiveStreamReplay(video);
}
/** Determine whether the livestream status is IDLE (not live yet) */
export function isLiveStreamIdle(video) {
    return video?.live_stream_status === Enum_LiveStream_Status.IDLE;
}
/** Determine whether the video is a re-stream of a live stream or another re-stream */
export function isLiveStreamRestream(video) {
    return !!video?.live_stream_video_source;
}
/**
 * Check if livestream switching between public/private messaging is enabled
 */
export const isLiveStreamPrivatePublicSwitchAvailable = (video) => {
    return Boolean(video &&
        !(video.live_stream_auto_response !==
            Enum_LiveStream_Auto_Response.DISABLED) &&
        ((video.live_stream_private_messaging ===
            Enum_LiveStream_Private_Messaging.ALWAYS_ON &&
            isLiveStreamLiveOrReplay(video)) ||
            (video.live_stream_private_messaging ===
                Enum_LiveStream_Private_Messaging.REPLAY_ONLY &&
                isLiveStreamReplay(video))));
};
/**
 * Livestream is idle (has not started yet, connection status is unknown)
 */
export const isStreamingIdle = (video, liveStreamState) => 
// When the live stream is idle, the live stream state is not set.
isLiveStream(video) &&
    liveStreamState === null &&
    video?.live_stream_status === Enum_LiveStream_Status.IDLE;
/**
 * Livestream is live (connecting, playing, paused - based on player state)
 */
export const isStreamingLive = (liveStreamState) => liveStreamState !== null &&
    [
        ILiveStreamState.ACTIVE_WAITING,
        ILiveStreamState.ACTIVE_PLAYING,
        ILiveStreamState.ACTIVE_PAUSED,
    ].includes(liveStreamState);
/**
 * Livestream is live (connected and playing or paused - based on player state)
 */
export const isStreamingLivePlaying = (liveStreamState) => liveStreamState !== null &&
    [ILiveStreamState.ACTIVE_PLAYING, ILiveStreamState.ACTIVE_PAUSED].includes(liveStreamState);
/**
 * Livestream replay is playing (based on player state)
 */
export const isStreamingReplay = (liveStreamState) => liveStreamState !== null &&
    ILiveStreamState.REPLAY_PLAYING === liveStreamState;
/**
 *
 */
export const isStreamingActiveOrReplay = (video, liveStreamState) => {
    return (isLiveStream(video) &&
        (isStreamingLive(liveStreamState) || isStreamingReplay(liveStreamState)));
};
/**
 * Checks if the video is being restreamed (used for scheduled/recurring showrooms).
 *
 * @param {Api_Video | null} video - The video object.
 * @param {ILiveStreamState | null} liveStreamState - The current livestream state.
 * @returns {boolean} - Returns true if the video is being restreamed; otherwise, false.
 */
export const isRestreaming = (video, liveStreamState) => {
    return (isLiveStream(video) &&
        !!video?.ref_video_id &&
        isStreamingLive(liveStreamState));
};
/**
 * Transform a chat message from API format to a WS (and stored) live stream message
 */
export const chatMessageToLiveStreamMessage = (chatMessage) => {
    const { id, text, user_type, username, elapsed_time, reply } = chatMessage;
    return {
        event_type: 'message',
        id,
        text,
        user_type,
        username,
        elapsed_time,
        reply,
    };
};
/**
 * Transform a private chat message from API format to a WS (and stored) live stream message
 */
export const chatPrivateMessageToLiveStreamPrivateMessage = (chatMessage) => {
    const { id, text, user_type, username, elapsed_time, reply } = chatMessage;
    return {
        event_type: 'private_message',
        id,
        text,
        user_type,
        username,
        elapsed_time,
        reply,
    };
};
/**
 *
 */
export const eventToLiveStreamEvent = (event) => {
    const { action_id, action_type, elapsed_time, user_type, payload } = event;
    return {
        event_type: action_type,
        id: action_id,
        payload,
        user_type,
        elapsed_time,
    };
};
/**
 * Sort messages by elapsed time (descending)
 */
export const byElapsedTimeDesc = (a, b) => {
    if (a.elapsed_time === undefined || b.elapsed_time === undefined) {
        return -1;
    }
    return a.elapsed_time >= b.elapsed_time ? 1 : -1;
};
/** Determine whether the product is unavailable for purchase within a ls replay */
export const isVideoReplayAndProductUnavailable = (video, product) => isLiveStreamReplay(video) &&
    product.available_for_live_stream_replay === false;
